import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

const CONSENT_COPY =
  "Yes, I would like to receive occasional emails from Sugar Town Organics about products, wellness guidance, special offers, events, workshops and brand stories. I understand that I can unsubscribe at any time. Sugar Town Organics will handle my information in accordance with its ";

const SUCCESS_MESSAGE =
  "Almost there — check your inbox and tap the confirmation link to join the House.";
const DUPLICATE_MESSAGE = "You're already part of the House. We're glad you're here.";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = "idle" | "submitting" | "success" | "duplicate";

const Newsletter = ({ source = "sugar_town_footer" }: { source?: string } = {}) => {
  const emailId = useId();
  const consentId = useId();
  const emailErrorId = `${emailId}-error`;
  const consentErrorId = `${consentId}-error`;

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    setFormError(null);
    const trimmed = email.trim();
    const nextEmailError = !trimmed
      ? "Please enter your email address."
      : !EMAIL_PATTERN.test(trimmed)
        ? "Please enter a valid email address."
        : null;
    const nextConsentError = consent ? null : "Please confirm your consent to continue.";

    setEmailError(nextEmailError);
    setConsentError(nextConsentError);
    if (nextEmailError || nextConsentError) {
      trackEvent("Newsletter Signup Error", {
        source,
        reason: nextEmailError ? "invalid_email" : "missing_consent",
      });
      return;
    }

    setStatus("submitting");
    try {
      const { data, error } = await supabase.functions.invoke("newsletter-signup", {
        body: {
          email: trimmed,
          consent: true,
          source,
          pageUrl: window.location.href,
          referrer: document.referrer || null,
          language: navigator.language,
        },
      });
      if (error) throw error;
      const duplicate = data?.status === "duplicate";
      setStatus(duplicate ? "duplicate" : "success");
      trackEvent("Newsletter Signup Success", {
        source,
        result: duplicate ? "duplicate" : "new",
      });
    } catch (err) {
      setStatus("idle");
      trackEvent("Newsletter Signup Error", {
        source,
        reason: "submission_failed",
        message: err instanceof Error ? err.message.slice(0, 120) : "unknown",
      });
      setFormError(
        "We couldn't complete your subscription just now. Please try again, or visit our newsletter page.",
      );
    }
  };

  const isDone = status === "success" || status === "duplicate";

  return (
    <section
      aria-label="Island newsletter"
      className="relative"
      style={{
        background:
          "linear-gradient(180deg, hsl(150 30% 12%) 0%, hsl(150 32% 9%) 100%)",
      }}
    >
      {/* Subtle gold botanical divider */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, hsl(40 55% 55% / 0.55) 50%, transparent 100%)",
        }}
      />
      <div
        className="container mx-auto px-6 py-9 md:py-11"
        style={{ maxWidth: "1100px" }}
      >
        <p className="font-serif italic text-[hsl(40_33%_94%)] text-sm md:text-[15px] leading-relaxed text-center mb-6 max-w-[560px] mx-auto">
          Receive occasional island dispatches, seasonal releases &amp; St. Kitts stories.
        </p>

        <div aria-live="polite" className="max-w-[720px] mx-auto">
          {isDone ? (
            <p className="font-serif italic text-center text-[hsl(40_33%_94%)] text-base leading-relaxed py-3">
              {status === "duplicate" ? DUPLICATE_MESSAGE : SUCCESS_MESSAGE}
            </p>
          ) : null}
          {formError ? (
            <p className="font-sans text-sm text-center text-[hsl(10_70%_78%)] mb-4">{formError}</p>
          ) : null}
        </div>

        {!isDone && (
          <form onSubmit={handleSubmit} noValidate className="max-w-[720px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
              <div className="flex-1">
                <label
                  htmlFor={emailId}
                  className="block font-sans text-[11px] uppercase tracking-[0.28em] text-accent mb-2"
                >
                  Email address
                </label>
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? emailErrorId : undefined}
                  placeholder="you@example.com"
                  className="w-full min-h-[44px] rounded-full bg-[hsl(40_33%_94%)] text-foreground placeholder:text-foreground/45 px-5 py-3 font-sans text-sm border border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(150_32%_9%)]"
                />
                {emailError ? (
                  <p id={emailErrorId} className="mt-2 font-sans text-xs text-[hsl(10_70%_78%)]">
                    {emailError}
                  </p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="md:mt-[30px] inline-flex items-center justify-center min-h-[44px] w-full md:w-auto bg-accent text-accent-foreground px-8 py-3 rounded-full font-sans font-medium text-[11px] uppercase tracking-[0.28em] hover:bg-accent/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(150_32%_9%)]"
              >
                {status === "submitting" ? "Joining…" : "Join the House"}
              </button>
            </div>

            <div className="mt-4 flex items-start gap-3">
              <input
                id={consentId}
                name="consent"
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  if (consentError) setConsentError(null);
                }}
                aria-invalid={consentError ? true : undefined}
                aria-describedby={consentError ? consentErrorId : undefined}
                className="mt-1 h-5 w-5 flex-shrink-0 rounded-[4px] accent-[hsl(40_55%_55%)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(150_32%_9%)]"
              />
              <label
                htmlFor={consentId}
                className="font-sans text-xs leading-relaxed text-[hsl(40_25%_82%)] cursor-pointer"
              >
                {CONSENT_COPY}
                <Link to="/privacy-policy" className="text-accent underline hover:text-accent/80">
                  Privacy Policy
                </Link>
                .
              </label>
            </div>
            {consentError ? (
              <p id={consentErrorId} className="mt-2 font-sans text-xs text-[hsl(10_70%_78%)]">
                {consentError}
              </p>
            ) : null}
          </form>
        )}
      </div>
    </section>
  );
};

export default Newsletter;
